/**
 * The Subtitle Window (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './tutorials/TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "The Subtitle Window",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Subtitle mode turns Sokuji into a slim floating subtitle bar that you can place over a meeting, a video or your slides. Screen sharing and OBS can capture it, so your audience can read the translation too. It works in the desktop app and in the browser extension."
  },
  "steps": [
    {
      "title": "Open it",
      "content": "Click <strong>Subtitle</strong> in the title bar. In the desktop app the main window turns into the subtitle bar, about 80% of the screen wide and 200 pixels tall near the bottom of the screen; Sokuji remembers where you put it.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "Place it, then start",
      "content": "Move and resize the bar where you want it, then click <strong>Start translating</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "Read the subtitles",
      "content": "New lines appear at the bottom. The toolbar has the same controls as the main window: which lines to show for Me and Other, text size, compact view, export and clear. In compact view, when the other side has two or more speakers (Soniox, with your own key or through KizunaAI), a numbered dot marks who is speaking.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "Adjust the look",
      "content": "The gear button opens <strong>Subtitle settings</strong>: background opacity, background colour, source and translation text colours, and <strong>Highlight newly-arrived text</strong>. These settings belong to the subtitle window and do not change the main window.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "Keep it in place",
      "content": "<strong>Always on top</strong> keeps it above other windows and <strong>Fullscreen</strong> fills the screen (both in the desktop app). <strong>Lock position and size</strong> stops accidental moves."
    },
    {
      "title": "Leave subtitle mode",
      "content": "In the desktop app, click <strong>Return to main window</strong> on the bar or press Esc; Esc first leaves fullscreen. The window returns to its previous size. In the browser extension, click <strong>Exit subtitle</strong> on the overlay."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "To show the subtitles to an audience, add a Window Capture source for the subtitle window in OBS, or share that window in your call.",
      "Show only the translation (Trans) when your audience does not read the original language.",
      "In the browser extension the subtitles appear as an overlay on the meeting page, which you can drag and resize."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "The Subtitle button is disabled in the extension.",
        "answer": "In the extension, subtitle mode needs a running session. Start the session first. If Sokuji asks you to refresh the meeting tab, reload it and try again."
      },
      {
        "question": "Do the subtitle settings change the main window?",
        "answer": "No. The subtitle window has its own text size, colours and display choices."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "字幕窗口",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "字幕模式会把 Sokuji 变成一个细长的浮动字幕条，你可以把它放在会议、视频或幻灯片上方。屏幕共享和 OBS 都能捕获它，因此你的观众也能看到翻译。桌面应用和浏览器扩展都支持此功能。"
  },
  "steps": [
    {
      "title": "打开字幕窗口",
      "content": "点击标题栏中的<strong>字幕</strong>。在桌面应用中，主窗口会变成字幕条，宽度约为屏幕的 80%，高度为 200 像素，位于屏幕底部附近；Sokuji 会记住你放置它的位置。",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "先摆好位置，再开始",
      "content": "将字幕条移动到你想要的位置并调整大小，然后点击<strong>开始翻译</strong>。",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "阅读字幕",
      "content": "新的内容会出现在底部。工具栏上的控件与主窗口相同：我和对方各显示哪些内容、字号、紧凑视图、导出和清除。在紧凑视图中，当对方有两个或更多说话人时（Soniox，使用自己的密钥或通过 KizunaAI），会用一个带编号的圆点标出是谁在说话。",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "调整外观",
      "content": "齿轮按钮会打开<strong>字幕设置</strong>：背景透明度、背景颜色、原文和译文的文字颜色，以及<strong>高亮显示新到达的文本</strong>。这些设置只属于字幕窗口，不会改变主窗口。",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "固定位置",
      "content": "<strong>置顶</strong>让它保持在其他窗口之上，<strong>全屏</strong>让它铺满屏幕（两者都仅限桌面应用）。<strong>锁定位置和尺寸</strong>可以防止误移动。"
    },
    {
      "title": "退出字幕模式",
      "content": "桌面应用中，点击字幕条上的<strong>返回主窗口</strong>按钮，或按 Esc；如果处于全屏状态，Esc 会先退出全屏。窗口会恢复到之前的大小。浏览器扩展中，点击浮层上的<strong>退出字幕</strong>。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "要向观众展示字幕，可以在 OBS 中为字幕窗口添加一个窗口捕获来源，或者在通话中共享该窗口。",
      "如果观众看不懂原文语言，请只显示译文（“译文”）。",
      "在浏览器扩展中，字幕会以浮层形式显示在会议页面上，你可以拖动它并调整大小。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "扩展中的“字幕”按钮不可用。",
        "answer": "在扩展中，字幕模式需要有正在运行的会话。请先开始会话。如果 Sokuji 要求你刷新会议标签页，请重新加载后再试。"
      },
      {
        "question": "字幕设置会改变主窗口吗？",
        "answer": "不会。字幕窗口有自己的字号、颜色和显示选项。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "字幕ウィンドウ",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "字幕モードでは、Sokuji が細長いフローティング字幕バーに変わり、会議や動画、スライドの上に置くことができます。画面共有や OBS で取り込めるので、視聴者も翻訳を読めます。デスクトップアプリでもブラウザー拡張機能でも使えます。"
  },
  "steps": [
    {
      "title": "開く",
      "content": "タイトルバーの<strong>字幕</strong>をクリックします。デスクトップアプリでは、メイン画面が字幕バーに変わります。幅は画面の約 80%、高さは 200 ピクセルで、画面下部の近くに表示されます。置いた位置は Sokuji が記憶します。",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "配置してから開始する",
      "content": "バーを好きな位置に移動してサイズを調整し、<strong>翻訳を開始</strong>をクリックします。",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "字幕を読む",
      "content": "新しい行は下に表示されます。ツールバーにはメイン画面と同じ操作があります。自分と相手それぞれに表示する行、文字サイズ、コンパクト表示、エクスポート、クリアです。コンパクト表示では、相手側に 2 人以上の話者がいる場合（Soniox。自分のキーまたは KizunaAI 経由）、番号付きの丸印で誰が話しているかを示します。",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "見た目を調整する",
      "content": "歯車ボタンで<strong>字幕設定</strong>が開きます。背景の透明度、背景色、原文と訳文の文字色、<strong>新しく届いたテキストをハイライトする</strong>を設定できます。これらは字幕ウィンドウ専用の設定で、メイン画面には影響しません。",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "位置を固定する",
      "content": "<strong>最前面に固定</strong>でほかのウィンドウより前に表示し続け、<strong>全画面表示</strong>で画面いっぱいに表示します（どちらもデスクトップアプリのみ）。<strong>位置とサイズをロック</strong>すると、うっかり動かしてしまうのを防げます。"
    },
    {
      "title": "字幕モードを終了する",
      "content": "デスクトップアプリでは、バーの<strong>メイン画面に戻る</strong>をクリックするか Esc キーを押します。全画面表示中は、Esc でまず全画面表示が解除されます。ウィンドウは元のサイズに戻ります。ブラウザ拡張機能では、オーバーレイの<strong>字幕を終了</strong>をクリックします。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "視聴者に字幕を見せるには、OBS で字幕ウィンドウのウィンドウキャプチャソースを追加するか、通話でそのウィンドウを共有します。",
      "視聴者が元の言語を読めない場合は、訳文だけ（「訳文」）を表示してください。",
      "ブラウザー拡張機能では、字幕は会議ページ上のオーバーレイとして表示され、ドラッグやサイズ変更ができます。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "拡張機能で「字幕」ボタンが押せません。",
        "answer": "拡張機能では、字幕モードを使うにはセッションが実行中である必要があります。先にセッションを開始してください。会議のタブを更新するよう Sokuji に求められた場合は、再読み込みしてからもう一度お試しください。"
      },
      {
        "question": "字幕設定はメイン画面にも反映されますか？",
        "answer": "いいえ。字幕ウィンドウには独自の文字サイズ、色、表示の設定があります。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "자막 창",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "자막 모드는 Sokuji를 회의, 동영상, 슬라이드 위에 띄워 둘 수 있는 얇은 플로팅 자막 바로 바꿉니다. 화면 공유와 OBS로 캡처할 수 있으므로 시청자도 번역을 읽을 수 있습니다. 데스크톱 앱과 브라우저 확장 프로그램 모두에서 작동합니다."
  },
  "steps": [
    {
      "title": "열기",
      "content": "제목 표시줄에서 <strong>자막</strong>을 클릭하세요. 데스크톱 앱에서는 메인 창이 화면 너비의 약 80%, 높이 200픽셀의 자막 바로 바뀌어 화면 아래쪽 근처에 나타납니다. Sokuji는 자막 바를 둔 위치를 기억합니다.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "배치한 다음 시작",
      "content": "바를 원하는 곳으로 옮기고 크기를 조정한 다음 <strong>번역 시작</strong>을 클릭하세요.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "자막 읽기",
      "content": "새 줄은 아래쪽에 나타납니다. 도구 모음에는 메인 창과 같은 컨트롤이 있습니다. 나와 상대방 각각 표시할 줄, 글자 크기, 간략 보기, 내보내기, 지우기입니다. 간략 보기에서는 상대방 쪽에 화자가 두 명 이상이면(Soniox, 내 API 키를 사용하거나 KizunaAI를 통해) 번호가 붙은 점으로 누가 말하고 있는지 표시합니다.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "모양 조정",
      "content": "톱니바퀴 버튼을 누르면 <strong>자막 설정</strong>이 열립니다. 배경 투명도, 배경색, 원문과 번역의 글자 색, <strong>새로 도착한 텍스트 강조 표시</strong>를 설정할 수 있습니다. 이 설정은 자막 창에만 적용되며 메인 창은 바뀌지 않습니다.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "위치 고정",
      "content": "<strong>항상 위</strong>는 다른 창보다 위에 표시하고, <strong>전체 화면</strong>은 화면을 가득 채웁니다(둘 다 데스크톱 앱 전용). <strong>위치 및 크기 잠금</strong>은 실수로 옮겨지는 것을 막습니다."
    },
    {
      "title": "자막 모드 나가기",
      "content": "데스크톱 앱에서는 자막 바의 <strong>메인 창으로 돌아가기</strong>를 클릭하거나 Esc를 누르세요. 전체 화면일 때는 Esc가 먼저 전체 화면을 해제합니다. 창은 이전 크기로 돌아갑니다. 브라우저 확장 프로그램에서는 오버레이의 <strong>자막 종료</strong>를 클릭하세요."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "시청자에게 자막을 보여 주려면 OBS에서 자막 창에 대한 창 캡처 소스를 추가하거나, 통화에서 그 창을 공유하세요.",
      "시청자가 원래 언어를 읽지 못한다면 번역만(“번역”) 표시하세요.",
      "브라우저 확장 프로그램에서는 자막이 회의 페이지 위에 오버레이로 표시되며, 끌어서 옮기고 크기를 조정할 수 있습니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "확장 프로그램에서 자막 버튼이 비활성화되어 있습니다.",
        "answer": "확장 프로그램에서 자막 모드를 쓰려면 세션이 실행 중이어야 합니다. 먼저 세션을 시작하세요. Sokuji가 회의 탭을 새로고침하라고 하면 새로고침한 뒤 다시 시도하세요."
      },
      {
        "question": "자막 설정이 메인 창도 바꾸나요?",
        "answer": "아니요. 자막 창에는 자체 글자 크기, 색상, 표시 설정이 있습니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "La ventana de subtítulos",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "El modo subtítulos convierte Sokuji en una barra de subtítulos flotante y estrecha que puedes colocar sobre una reunión, un vídeo o tus diapositivas. Compartir pantalla y OBS pueden capturarla, así que tu público también puede leer la traducción. Funciona en la aplicación de escritorio y en la extensión de navegador."
  },
  "steps": [
    {
      "title": "Ábrela",
      "content": "Haz clic en <strong>Subtítulos</strong> en la barra de título. En la aplicación de escritorio, la ventana principal se convierte en la barra de subtítulos, de un 80 % del ancho de la pantalla y 200 píxeles de alto, cerca de la parte inferior de la pantalla; Sokuji recuerda dónde la colocas.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "Colócala y empieza",
      "content": "Mueve la barra y cambia su tamaño donde quieras, y luego haz clic en <strong>Empezar a traducir</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "Lee los subtítulos",
      "content": "Las líneas nuevas aparecen abajo. La barra de herramientas tiene los mismos controles que la ventana principal: qué líneas mostrar para Yo y Otro, tamaño del texto, vista compacta, exportar y borrar. En la vista compacta, cuando la otra parte tiene dos o más hablantes (Soniox, con tu propia clave o a través de KizunaAI), un punto numerado indica quién habla.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "Ajusta el aspecto",
      "content": "El botón del engranaje abre <strong>Ajustes de subtítulos</strong>: opacidad del fondo, color del fondo, colores del texto original y de la traducción, y <strong>Resaltar texto recién llegado</strong>. Estos ajustes pertenecen a la ventana de subtítulos y no cambian la ventana principal.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "Mantenla en su sitio",
      "content": "<strong>Siempre visible</strong> la mantiene por encima de otras ventanas y <strong>Pantalla completa</strong> llena la pantalla (ambos en la aplicación de escritorio). <strong>Bloquear posición y tamaño</strong> evita movimientos accidentales."
    },
    {
      "title": "Sal del modo subtítulos",
      "content": "En la aplicación de escritorio, haz clic en <strong>Volver a la ventana principal</strong> en la barra o pulsa Esc; Esc primero sale de la pantalla completa. La ventana vuelve a su tamaño anterior. En la extensión de navegador, haz clic en <strong>Salir de subtítulos</strong> en la superposición."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Para mostrar los subtítulos a un público, añade en OBS una fuente de Captura de ventana para la ventana de subtítulos, o comparte esa ventana en tu llamada.",
      "Muestra solo la traducción (Traducción) cuando tu público no lea el idioma original.",
      "En la extensión de navegador, los subtítulos aparecen superpuestos en la página de la reunión, y puedes arrastrarlos y cambiar su tamaño."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "El botón Subtítulos está desactivado en la extensión.",
        "answer": "En la extensión, el modo subtítulos necesita una sesión en marcha. Inicia primero la sesión. Si Sokuji te pide actualizar la pestaña de la reunión, recárgala y vuelve a intentarlo."
      },
      {
        "question": "¿Los ajustes de subtítulos cambian la ventana principal?",
        "answer": "No. La ventana de subtítulos tiene su propio tamaño de texto, sus propios colores y sus propias opciones de visualización."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "La fenêtre de sous-titres",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Le mode sous-titres transforme Sokuji en une fine barre de sous-titres flottante que vous pouvez placer au-dessus d’une réunion, d’une vidéo ou de vos diapositives. Le partage d’écran et OBS peuvent la capturer, pour que votre public puisse lire la traduction lui aussi. Il fonctionne dans l’application de bureau et dans l’extension de navigateur."
  },
  "steps": [
    {
      "title": "Ouvrez-la",
      "content": "Cliquez sur <strong>Sous-titres</strong> dans la barre de titre. Dans l’application de bureau, la fenêtre principale devient la barre de sous-titres, large d’environ 80 % de l’écran et haute de 200 pixels, près du bas de l’écran ; Sokuji mémorise l’endroit où vous la placez.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "Placez-la, puis démarrez",
      "content": "Déplacez et redimensionnez la barre où vous le souhaitez, puis cliquez sur <strong>Démarrer la traduction</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "Lisez les sous-titres",
      "content": "Les nouvelles lignes apparaissent en bas. La barre d’outils offre les mêmes commandes que la fenêtre principale : les lignes à afficher pour Moi et Autre, la taille du texte, la vue compacte, l’export et l’effacement. En vue compacte, quand il y a deux intervenants ou plus de l’autre côté (Soniox, avec votre propre clé ou via KizunaAI), un point numéroté indique qui parle.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "Réglez l’apparence",
      "content": "Le bouton en forme d’engrenage ouvre les <strong>Paramètres des sous-titres</strong> : opacité du fond, couleur du fond, couleurs du texte source et de la traduction, et <strong>Surligner le texte nouvellement arrivé</strong>. Ces réglages appartiennent à la fenêtre de sous-titres et ne modifient pas la fenêtre principale.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "Gardez-la en place",
      "content": "<strong>Toujours au premier plan</strong> la garde au-dessus des autres fenêtres et <strong>Plein écran</strong> remplit l’écran (tous deux dans l’application de bureau). <strong>Verrouiller la position et la taille</strong> évite les déplacements accidentels."
    },
    {
      "title": "Quittez le mode sous-titres",
      "content": "Dans l’application de bureau, cliquez sur <strong>Retour à la fenêtre principale</strong> dans la barre ou appuyez sur Échap ; Échap quitte d’abord le plein écran. La fenêtre reprend sa taille précédente. Dans l’extension de navigateur, cliquez sur <strong>Quitter les sous-titres</strong> sur la superposition."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Pour montrer les sous-titres à un public, ajoutez dans OBS une source Capture de fenêtre pour la fenêtre de sous-titres, ou partagez cette fenêtre dans votre appel.",
      "N’affichez que la traduction (Traduction) quand votre public ne lit pas la langue d’origine.",
      "Dans l’extension de navigateur, les sous-titres s’affichent en superposition sur la page de la réunion ; vous pouvez les déplacer et les redimensionner."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Le bouton Sous-titres est désactivé dans l’extension.",
        "answer": "Dans l’extension, le mode sous-titres nécessite une session en cours. Démarrez d’abord la session. Si Sokuji vous demande d’actualiser l’onglet de la réunion, rechargez-le et réessayez."
      },
      {
        "question": "Les paramètres des sous-titres modifient-ils la fenêtre principale ?",
        "answer": "Non. La fenêtre de sous-titres a sa propre taille de texte, ses propres couleurs et ses propres choix d’affichage."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "نافذة الترجمة النصية",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "يحوّل وضع الترجمة Sokuji إلى شريط ترجمة نصية عائم ونحيف يمكنك وضعه فوق اجتماع أو فيديو أو شرائح عرضك. يمكن لمشاركة الشاشة وOBS التقاطه، فيستطيع جمهورك قراءة الترجمة أيضًا. يعمل في تطبيق سطح المكتب وفي امتداد المتصفح."
  },
  "steps": [
    {
      "title": "افتحها",
      "content": "انقر على <strong>ترجمة</strong> في شريط العنوان. في تطبيق سطح المكتب تتحول النافذة الرئيسية إلى شريط الترجمة، بعرض نحو 80% من الشاشة وارتفاع 200 بكسل قرب أسفل الشاشة؛ ويتذكر Sokuji المكان الذي وضعته فيه.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "ضعها في مكانها، ثم ابدأ",
      "content": "حرّك الشريط وغيّر حجمه كما تريد، ثم انقر على <strong>بدء الترجمة</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "اقرأ الترجمة النصية",
      "content": "تظهر الأسطر الجديدة في الأسفل. يحتوي شريط الأدوات على عناصر التحكم نفسها الموجودة في النافذة الرئيسية: الأسطر المعروضة لـ«أنا» و«الآخر»، وحجم النص، والعرض المضغوط، والتصدير، والمسح. وفي العرض المضغوط، عندما يكون في الطرف الآخر متحدثان أو أكثر (Soniox، بمفتاحك الخاص أو عبر KizunaAI)، تشير نقطة مرقّمة إلى من يتحدث.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "اضبط المظهر",
      "content": "يفتح زر الترس <strong>إعدادات الترجمة</strong>: شفافية الخلفية، ولون الخلفية، وألوان النص الأصلي والترجمة، و<strong>تمييز النص الذي وصل حديثًا</strong>. تخص هذه الإعدادات نافذة الترجمة ولا تغيّر النافذة الرئيسية.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "ثبّتها في مكانها",
      "content": "يُبقيها <strong>دائمًا في المقدمة</strong> فوق النوافذ الأخرى، ويجعلها <strong>ملء الشاشة</strong> تملأ الشاشة (كلاهما في تطبيق سطح المكتب). ويمنع <strong>قفل الموضع والحجم</strong> تحريكها عن طريق الخطأ."
    },
    {
      "title": "اخرج من وضع الترجمة",
      "content": "في تطبيق سطح المكتب، انقر على <strong>العودة إلى النافذة الرئيسية</strong> في الشريط أو اضغط Esc؛ إذا كانت في وضع ملء الشاشة، فإن Esc يخرج منه أولًا. تعود النافذة إلى حجمها السابق. وفي امتداد المتصفح، انقر على <strong>إيقاف الترجمة</strong> في الطبقة العلوية."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "لعرض الترجمة النصية على جمهور، أضف مصدر التقاط نافذة (Window Capture) لنافذة الترجمة في OBS، أو شارك تلك النافذة في مكالمتك.",
      "اعرض الترجمة وحدها («الترجمة») عندما لا يقرأ جمهورك اللغة الأصلية.",
      "في امتداد المتصفح تظهر الترجمة النصية كطبقة فوق صفحة الاجتماع، ويمكنك سحبها وتغيير حجمها."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "زر «ترجمة» معطّل في الامتداد.",
        "answer": "في الامتداد، يحتاج وضع الترجمة إلى جلسة جارية. ابدأ الجلسة أولًا. إذا طلب منك Sokuji تحديث علامة تبويب الاجتماع، فأعد تحميلها وحاول مرة أخرى."
      },
      {
        "question": "هل تغيّر إعدادات الترجمة النافذة الرئيسية؟",
        "answer": "لا. لنافذة الترجمة حجم نص وألوان وخيارات عرض خاصة بها."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "A janela de legendas",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "O modo de legendas transforma o Sokuji em uma barra de legendas flutuante e estreita, que você pode posicionar sobre uma reunião, um vídeo ou seus slides. O compartilhamento de tela e o OBS conseguem capturá-la, para que o seu público também leia a tradução. Funciona no aplicativo de desktop e na extensão de navegador."
  },
  "steps": [
    {
      "title": "Abra a janela",
      "content": "Clique em <strong>Legendas</strong> na barra de título. No aplicativo de desktop, a janela principal vira a barra de legendas, com cerca de 80% da largura da tela e 200 pixels de altura, perto da parte inferior da tela; o Sokuji lembra onde você a colocou.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "Posicione e comece",
      "content": "Mova e redimensione a barra onde quiser e clique em <strong>Iniciar tradução</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "Leia as legendas",
      "content": "As novas falas aparecem embaixo. A barra de ferramentas tem os mesmos controles da janela principal: quais falas mostrar para Eu e Outro, tamanho do texto, visualização compacta, exportar e limpar. Na visualização compacta, quando o outro lado tem dois ou mais falantes (Soniox, com a sua própria chave ou pela KizunaAI), um ponto numerado indica quem está falando.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "Ajuste a aparência",
      "content": "O botão de engrenagem abre <strong>Configurações de legendas</strong>: opacidade do fundo, cor do fundo, cores do texto original e da tradução, e <strong>Destacar texto recém-chegado</strong>. Essas configurações pertencem à janela de legendas e não alteram a janela principal.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "Mantenha no lugar",
      "content": "<strong>Sempre no topo</strong> a mantém acima das outras janelas e <strong>Tela cheia</strong> ocupa a tela inteira (ambos no aplicativo de desktop). <strong>Bloquear posição e tamanho</strong> evita movimentos acidentais."
    },
    {
      "title": "Saia do modo de legendas",
      "content": "No aplicativo de desktop, clique em <strong>Voltar à janela principal</strong> na barra ou pressione Esc; o Esc primeiro sai da tela cheia. A janela volta ao tamanho anterior. Na extensão de navegador, clique em <strong>Sair das legendas</strong> na sobreposição."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Para mostrar as legendas a um público, adicione no OBS uma fonte de Captura de janela para a janela de legendas, ou compartilhe essa janela na chamada.",
      "Mostre só a tradução (Tradução) quando o seu público não lê o idioma original.",
      "Na extensão de navegador, as legendas aparecem sobrepostas à página da reunião, e você pode arrastá-las e redimensioná-las."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "O botão Legendas está desativado na extensão.",
        "answer": "Na extensão, o modo de legendas precisa de uma sessão em andamento. Inicie a sessão primeiro. Se o Sokuji pedir para atualizar a aba da reunião, recarregue-a e tente de novo."
      },
      {
        "question": "As configurações de legendas alteram a janela principal?",
        "answer": "Não. A janela de legendas tem seu próprio tamanho de texto, suas próprias cores e suas próprias opções de exibição."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Окно субтитров",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Режим субтитров превращает Sokuji в тонкую плавающую строку субтитров, которую можно расположить поверх встречи, видео или слайдов. Её можно захватить при демонстрации экрана и в OBS, так что ваша аудитория тоже сможет читать перевод. Режим работает в настольном приложении и в расширении для браузера."
  },
  "steps": [
    {
      "title": "Откройте его",
      "content": "Нажмите <strong>Субтитры</strong> в строке заголовка. В настольном приложении главное окно превращается в строку субтитров шириной около 80% экрана и высотой 200 пикселей у нижнего края экрана; Sokuji запоминает, куда вы её поместили.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "Расположите, затем начните",
      "content": "Переместите строку и измените её размер как нужно, затем нажмите <strong>Начать перевод</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "Читайте субтитры",
      "content": "Новые строки появляются внизу. На панели инструментов те же элементы, что и в главном окне: какие строки показывать для «Я» и «Собеседник», размер текста, компактный вид, экспорт и очистка. В компактном виде, когда на стороне собеседника двое или более говорящих (Soniox — с вашим ключом или через KizunaAI), пронумерованная точка показывает, кто говорит.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "Настройте внешний вид",
      "content": "Кнопка с шестерёнкой открывает <strong>Настройки субтитров</strong>: прозрачность фона, цвет фона, цвета исходного текста и перевода, а также <strong>Подсвечивать только что поступивший текст</strong>. Эти настройки относятся к окну субтитров и не меняют главное окно.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "Закрепите на месте",
      "content": "<strong>Поверх всех окон</strong> держит его над другими окнами, а <strong>Полноэкранный режим</strong> разворачивает на весь экран (оба — в настольном приложении). <strong>Зафиксировать позицию и размер</strong> защищает от случайного перемещения."
    },
    {
      "title": "Выйдите из режима субтитров",
      "content": "В настольном приложении нажмите <strong>Вернуться в главное окно</strong> на строке субтитров или клавишу Esc; сначала Esc выходит из полноэкранного режима. Окно возвращается к прежнему размеру. В расширении для браузера нажмите <strong>Выключить субтитры</strong> на наложении субтитров."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Чтобы показать субтитры аудитории, добавьте в OBS источник «Захват окна» для окна субтитров или покажите это окно в звонке.",
      "Показывайте только перевод («Перевод»), если ваша аудитория не читает на исходном языке.",
      "В расширении для браузера субтитры появляются поверх страницы встречи; их можно перетаскивать и менять размер."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Кнопка «Субтитры» в расширении недоступна.",
        "answer": "В расширении режиму субтитров нужен запущенный сеанс. Сначала запустите сеанс. Если Sokuji просит обновить вкладку встречи, перезагрузите её и попробуйте снова."
      },
      {
        "question": "Меняют ли настройки субтитров главное окно?",
        "answer": "Нет. У окна субтитров свой размер текста, свои цвета и свои параметры отображения."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Das Untertitelfenster",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Der Untertitel-Modus verwandelt Sokuji in eine schmale, schwebende Untertitelleiste, die Sie über ein Meeting, ein Video oder Ihre Folien legen können. Bildschirmfreigabe und OBS können sie erfassen, sodass auch Ihr Publikum die Übersetzung lesen kann. Er funktioniert in der Desktop-App und in der Browsererweiterung."
  },
  "steps": [
    {
      "title": "Öffnen",
      "content": "Klicken Sie in der Titelleiste auf <strong>Untertitel</strong>. In der Desktop-App wird das Hauptfenster zur Untertitelleiste, etwa 80 % der Bildschirmbreite breit und 200 Pixel hoch, nahe am unteren Bildschirmrand; Sokuji merkt sich, wo Sie sie platziert haben.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "Platzieren, dann starten",
      "content": "Verschieben Sie die Leiste und passen Sie ihre Größe an, dann klicken Sie auf <strong>Übersetzung starten</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "Die Untertitel lesen",
      "content": "Neue Zeilen erscheinen unten. Die Symbolleiste hat dieselben Bedienelemente wie das Hauptfenster: welche Zeilen für „Ich“ und „Gegenüber“ angezeigt werden, Textgröße, kompakte Ansicht, Export und Löschen. Wenn in der kompakten Ansicht auf der anderen Seite zwei oder mehr Personen sprechen (Soniox, mit eigenem Schlüssel oder über KizunaAI), markiert ein nummerierter Punkt, wer gerade spricht.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "Das Aussehen anpassen",
      "content": "Die Zahnrad-Schaltfläche öffnet die <strong>Untertitel-Einstellungen</strong>: Hintergrund-Transparenz, Hintergrundfarbe, Farben für Quelltext und Übersetzung sowie <strong>Neu eingetroffenen Text hervorheben</strong>. Diese Einstellungen gehören zum Untertitelfenster und ändern das Hauptfenster nicht.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "An Ort und Stelle halten",
      "content": "<strong>Immer im Vordergrund</strong> hält es über anderen Fenstern, und <strong>Vollbild</strong> füllt den Bildschirm (beides in der Desktop-App). <strong>Position und Größe sperren</strong> verhindert versehentliches Verschieben."
    },
    {
      "title": "Den Untertitel-Modus verlassen",
      "content": "Klicken Sie in der Desktop-App in der Leiste auf <strong>Zurück zum Hauptfenster</strong> oder drücken Sie Esc; Esc verlässt zuerst den Vollbildmodus. Das Fenster kehrt zu seiner vorherigen Größe zurück. In der Browsererweiterung klicken Sie im Overlay auf <strong>Untertitel beenden</strong>."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Um die Untertitel einem Publikum zu zeigen, fügen Sie in OBS eine Fensteraufnahme-Quelle für das Untertitelfenster hinzu oder geben Sie dieses Fenster in Ihrem Anruf frei.",
      "Zeigen Sie nur die Übersetzung („Übersetzung“), wenn Ihr Publikum die Originalsprache nicht liest.",
      "In der Browsererweiterung erscheinen die Untertitel als Overlay auf der Meeting-Seite; Sie können es verschieben und seine Größe ändern."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Die Schaltfläche „Untertitel“ ist in der Erweiterung deaktiviert.",
        "answer": "In der Erweiterung braucht der Untertitel-Modus eine laufende Sitzung. Starten Sie zuerst die Sitzung. Wenn Sokuji Sie bittet, den Meeting-Tab zu aktualisieren, laden Sie ihn neu und versuchen Sie es erneut."
      },
      {
        "question": "Ändern die Untertitel-Einstellungen das Hauptfenster?",
        "answer": "Nein. Das Untertitelfenster hat eigene Textgröße, Farben und Anzeigeoptionen."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Вікно субтитрів",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Режим субтитрів перетворює Sokuji на тонкий плаваючий рядок субтитрів, який можна розмістити поверх зустрічі, відео чи слайдів. Його можна захопити під час демонстрації екрана та в OBS, тож ваша аудиторія теж зможе читати переклад. Режим працює в настільному застосунку та в розширенні для браузера."
  },
  "steps": [
    {
      "title": "Відкрийте його",
      "content": "Натисніть <strong>Субтитри</strong> в рядку заголовка. У настільному застосунку головне вікно перетворюється на рядок субтитрів завширшки близько 80% екрана й заввишки 200 пікселів біля нижнього краю екрана; Sokuji запам’ятовує, куди ви його розмістили.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "Розмістіть, потім почніть",
      "content": "Перемістіть рядок і змініть його розмір як потрібно, потім натисніть <strong>Почати переклад</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "Читайте субтитри",
      "content": "Нові рядки з’являються внизу. На панелі інструментів ті самі елементи, що й у головному вікні: які рядки показувати для «Я» та «Співрозмовник», розмір тексту, компактний вигляд, експорт і очищення. У компактному вигляді, коли на боці співрозмовника двоє або більше мовців (Soniox — з вашим власним ключем або через KizunaAI), пронумерована крапка показує, хто говорить.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "Налаштуйте вигляд",
      "content": "Кнопка з шестернею відкриває <strong>Налаштування субтитрів</strong>: прозорість тла, колір тла, кольори вихідного тексту й перекладу, а також <strong>Підсвічувати щойно отриманий текст</strong>. Ці налаштування стосуються вікна субтитрів і не змінюють головне вікно.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "Закріпіть на місці",
      "content": "<strong>Поверх усіх вікон</strong> тримає його над іншими вікнами, а <strong>Повний екран</strong> розгортає на весь екран (обидва — у настільному застосунку). <strong>Закріпити позицію та розмір</strong> захищає від випадкового переміщення."
    },
    {
      "title": "Вийдіть із режиму субтитрів",
      "content": "У настільному застосунку натисніть <strong>Повернутися до головного вікна</strong> на рядку субтитрів або клавішу Esc; спочатку Esc виходить із повноекранного режиму. Вікно повертається до попереднього розміру. У розширенні для браузера натисніть <strong>Вийти з субтитрів</strong> на накладці."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Щоб показати субтитри аудиторії, додайте в OBS джерело «Захоплення вікна» для вікна субтитрів або покажіть це вікно у дзвінку.",
      "Показуйте лише переклад («Переклад»), якщо ваша аудиторія не читає мовою оригіналу.",
      "У розширенні для браузера субтитри з’являються поверх сторінки зустрічі; їх можна перетягувати й змінювати розмір."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Кнопка «Субтитри» в розширенні недоступна.",
        "answer": "У розширенні режиму субтитрів потрібен запущений сеанс. Спочатку запустіть сеанс. Якщо Sokuji просить оновити вкладку зустрічі, перезавантажте її та спробуйте знову."
      },
      {
        "question": "Чи змінюють налаштування субтитрів головне вікно?",
        "answer": "Ні. Вікно субтитрів має власний розмір тексту, власні кольори й власні параметри відображення."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "La finestra dei sottotitoli",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "La modalità sottotitoli trasforma Sokuji in una sottile barra di sottotitoli fluttuante, da posizionare sopra una riunione, un video o le tue slide. La condivisione dello schermo e OBS possono acquisirla, così anche il tuo pubblico può leggere la traduzione. Funziona nell’app desktop e nell’estensione per browser."
  },
  "steps": [
    {
      "title": "Aprila",
      "content": "Fai clic su <strong>Sottotitoli</strong> nella barra del titolo. Nell’app desktop la finestra principale diventa la barra dei sottotitoli, larga circa l’80% dello schermo e alta 200 pixel, vicino al bordo inferiore dello schermo; Sokuji ricorda dove la posizioni.",
      "screenshot": "01-subtitle-button.png"
    },
    {
      "title": "Posizionala, poi avvia",
      "content": "Sposta e ridimensiona la barra dove preferisci, poi fai clic su <strong>Avvia la traduzione</strong>.",
      "screenshot": "02-subtitle-window-idle.png"
    },
    {
      "title": "Leggi i sottotitoli",
      "content": "Le nuove righe compaiono in basso. La barra degli strumenti ha gli stessi controlli della finestra principale: quali righe mostrare per Io e Altro, dimensione del testo, vista compatta, esportazione e cancellazione. Nella vista compatta, quando dall’altra parte ci sono due o più interlocutori (Soniox, con la tua chiave o tramite KizunaAI), un punto numerato indica chi sta parlando.",
      "screenshot": "03-subtitle-window-live.png"
    },
    {
      "title": "Regola l’aspetto",
      "content": "Il pulsante a forma di ingranaggio apre <strong>Impostazioni sottotitoli</strong>: opacità dello sfondo, colore dello sfondo, colori del testo originale e della traduzione, e <strong>Evidenzia il testo appena arrivato</strong>. Queste impostazioni appartengono alla finestra dei sottotitoli e non modificano la finestra principale.",
      "screenshot": "04-subtitle-settings.png"
    },
    {
      "title": "Tienila in posizione",
      "content": "<strong>Sempre in primo piano</strong> la tiene sopra le altre finestre e <strong>Schermo intero</strong> riempie lo schermo (entrambi nell’app desktop). <strong>Blocca posizione e dimensioni</strong> evita spostamenti accidentali."
    },
    {
      "title": "Esci dalla modalità sottotitoli",
      "content": "Nell’app desktop, fai clic su <strong>Torna alla finestra principale</strong> sulla barra o premi Esc; Esc esce prima dallo schermo intero. La finestra torna alle dimensioni precedenti. Nell’estensione del browser, fai clic su <strong>Esci dai sottotitoli</strong> sull’overlay."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Per mostrare i sottotitoli a un pubblico, aggiungi in OBS una sorgente Cattura finestra per la finestra dei sottotitoli, oppure condividi quella finestra nella chiamata.",
      "Mostra solo la traduzione (Traduzione) quando il tuo pubblico non legge la lingua originale.",
      "Nell’estensione per browser i sottotitoli compaiono in sovrimpressione sulla pagina della riunione, e puoi trascinarli e ridimensionarli."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Il pulsante Sottotitoli è disattivato nell’estensione.",
        "answer": "Nell’estensione, la modalità sottotitoli richiede una sessione attiva. Avvia prima la sessione. Se Sokuji ti chiede di aggiornare la scheda della riunione, ricaricala e riprova."
      },
      {
        "question": "Le impostazioni dei sottotitoli modificano la finestra principale?",
        "answer": "No. La finestra dei sottotitoli ha la sua dimensione del testo, i suoi colori e le sue opzioni di visualizzazione."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function SubtitleMode() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/subtitle-mode" />;
}
